import React, { useMemo, useState } from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import Mandala from "../components/decor/Mandala";
import { toast } from "sonner";
import {
  CalendarDays,
  BedDouble,
  Users,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Printer,
  Copy,
} from "lucide-react";
import { ROOM_TYPES, ROOM_INVENTORY, REFUNDABLE_DEPOSIT, BOOKING_RULES } from "../data/rooms";
import { IMG } from "../data/site";
import { useLang } from "../i18n/LanguageContext";

const STORAGE_KEY = "sode_room_bookings_v1";

const StepPill = ({ n, label, active, done }) => (
  <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
    <div
      className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold border ${
        done
          ? "bg-[#B65A14] text-[#FBF7F0] border-[#B65A14]"
          : active
          ? "bg-[#D4AF37] text-[#3B2A1A] border-[#D4AF37]"
          : "bg-[#FBF7F0] text-[#3B2A1A]/60 border-[#D4AF37]/40"
      }`}
    >
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </div>
    <span className={`min-w-0 text-[11px] sm:text-sm leading-tight ${active || done ? "text-[#3B2A1A] font-semibold" : "text-[#3B2A1A]/60"}`}>
      {label}
    </span>
  </div>
);

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const formatLongDate = (iso, lang = "en") => {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "kn" ? "kn-IN" : "en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
const addHours = (iso, hrs) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setHours(d.getHours() + hrs);
  return d;
};

const genBookingId = () => {
  const s = "SODE-" + Math.random().toString(36).slice(2, 8).toUpperCase();
  return s;
};

const loadBookings = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};
const saveBookings = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const COPY = {
  chooseDate: { en: "Choose date", kn: "ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ" },
  selectRoom: { en: "Select room", kn: "ಕೊಠಡಿ ಆಯ್ಕೆಮಾಡಿ" },
  yourDetails: { en: "Your details", kn: "ನಿಮ್ಮ ವಿವರಗಳು" },
  confirmation: { en: "Confirmation", kn: "ದೃಢೀಕರಣ" },
  step1: { en: "Step 1 of 4", kn: "ಹಂತ 1 / 4" },
  step2: { en: "Step 2 of 4", kn: "ಹಂತ 2 / 4" },
  step3: { en: "Step 3 of 4", kn: "ಹಂತ 3 / 4" },
  chooseCheckIn: { en: "Choose your check-in date", kn: "ಚೆಕ್-ಇನ್ ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ" },
  checkInDate: { en: "Check-in date", kn: "ಚೆಕ್-ಇನ್ ದಿನಾಂಕ" },
  checkoutInfo: { en: "Check-out is 23 hours after check-in. Booking window:", kn: "ಚೆಕ್-ಇನ್ ಆದ 23 ಗಂಟೆಗಳ ನಂತರ ಚೆಕ್-ಔಟ್. ಬುಕಿಂಗ್ ಅವಧಿ:" },
  checkAvailability: { en: "Check Availability", kn: "ಲಭ್ಯತೆ ಪರಿಶೀಲಿಸಿ" },
  availableRoomsFor: { en: "Available rooms for", kn: "ಲಭ್ಯವಿರುವ ಕೊಠಡಿಗಳು" },
  roomRate: { en: "Room rate", kn: "ಕೊಠಡಿ ದರ" },
  upTo: { en: "Up to", kn: "ಗರಿಷ್ಠ" },
  stay23: { en: "23-hr stay", kn: "23 ಗಂಟೆಗಳ ವಾಸ" },
  per23: { en: "/ 23 hrs", kn: "/ 23 ಗಂಟೆ" },
  soldOut: { en: "Sold out", kn: "ಲಭ್ಯವಿಲ್ಲ" },
  available: { en: "available", kn: "ಲಭ್ಯ" },
  unavailable: { en: "Unavailable", kn: "ಲಭ್ಯವಿಲ್ಲ" },
  changeDate: { en: "Change date", kn: "ದಿನಾಂಕ ಬದಲಿಸಿ" },
  bookingDetails: { en: "Your booking details", kn: "ನಿಮ್ಮ ಬುಕಿಂಗ್ ವಿವರಗಳು" },
  fullName: { en: "Full name", kn: "ಪೂರ್ಣ ಹೆಸರು" },
  namePlaceholder: { en: "Devotee's name", kn: "ಭಕ್ತರ ಹೆಸರು" },
  email: { en: "Email address", kn: "ಇಮೇಲ್ ವಿಳಾಸ" },
  phone: { en: "Phone number", kn: "ದೂರವಾಣಿ ಸಂಖ್ಯೆ" },
  important: { en: "Important - please read", kn: "ಮುಖ್ಯ ಸೂಚನೆ - ದಯವಿಟ್ಟು ಓದಿ" },
  back: { en: "Back", kn: "ಹಿಂದೆ" },
  confirmBooking: { en: "Confirm Booking", kn: "ಬುಕಿಂಗ್ ದೃಢೀಕರಿಸಿ" },
  bookingSummary: { en: "Booking summary", kn: "ಬುಕಿಂಗ್ ಸಾರಾಂಶ" },
  room: { en: "Room", kn: "ಕೊಠಡಿ" },
  checkIn: { en: "Check-in", kn: "ಚೆಕ್-ಇನ್" },
  checkOut: { en: "Check-out", kn: "ಚೆಕ್-ಔಟ್" },
  duration: { en: "Duration", kn: "ಅವಧಿ" },
  depositAtCheckIn: { en: "Deposit (at check-in)", kn: "ಠೇವಣಿ (ಚೆಕ್-ಇನ್ ವೇಳೆ)" },
  payableNow: { en: "Payable now", kn: "ಈಗ ಪಾವತಿಸಬೇಕಾದುದು" },
  depositNote: { en: "The refundable deposit of", kn: "ಮರುಪಾವತಿಸಬಹುದಾದ ಠೇವಣಿ" },
  depositNoteEnd: { en: "is collected at the check-in counter, not online.", kn: "ಚೆಕ್-ಇನ್ ಕೌಂಟರ್‌ನಲ್ಲಿ ಪಡೆಯಲಾಗುತ್ತದೆ, ಆನ್‌ಲೈನ್‌ನಲ್ಲಿ ಅಲ್ಲ." },
  bookingConfirmed: { en: "Booking confirmed!", kn: "ಬುಕಿಂಗ್ ದೃಢೀಕರಿಸಲಾಗಿದೆ!" },
  bookingId: { en: "Booking ID", kn: "ಬುಕಿಂಗ್ ಸಂಖ್ಯೆ" },
  copy: { en: "Copy", kn: "ನಕಲಿಸಿ" },
  guest: { en: "Guest", kn: "ಅತಿಥಿ" },
  roomFare: { en: "Room fare", kn: "ಕೊಠಡಿ ದರ" },
  hours23: { en: "23 hours", kn: "23 ಗಂಟೆಗಳು" },
  depositPayable: { en: "A refundable deposit of", kn: "ಮರುಪಾವತಿಸಬಹುದಾದ ಠೇವಣಿ" },
  depositPayableEnd: { en: "is payable at the check-in counter.", kn: "ಚೆಕ್-ಇನ್ ಕೌಂಟರ್‌ನಲ್ಲಿ ಪಾವತಿಸಬೇಕು." },
  carryProof: { en: "Please carry a printed or screenshot copy of this confirmation and a valid photo ID. Check-out is exactly 23 hours after check-in - extensions are not allowed.", kn: "ದಯವಿಟ್ಟು ಈ ದೃಢೀಕರಣದ ಮುದ್ರಿತ ಪ್ರತಿಯನ್ನು ಅಥವಾ ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಅನ್ನು ಹಾಗೂ ಮಾನ್ಯ ಫೋಟೋ ಐಡಿಯನ್ನು ತರಿರಿ. ಚೆಕ್-ಇನ್ ಆದ ನಿಖರವಾಗಿ 23 ಗಂಟೆಗಳ ನಂತರ ಚೆಕ್-ಔಟ್ - ವಿಸ್ತರಣೆಗಳಿಗೆ ಅವಕಾಶವಿಲ್ಲ." },
  printConfirmation: { en: "Print confirmation", kn: "ದೃಢೀಕರಣ ಮುದ್ರಿಸಿ" },
  anotherBooking: { en: "Make another booking", kn: "ಮತ್ತೊಂದು ಬುಕಿಂಗ್ ಮಾಡಿ" },
  dateRangeError: { en: "Please choose a date between 3 and 30 days from today.", kn: "ದಯವಿಟ್ಟು ಇಂದಿನಿಂದ 3 ರಿಂದ 30 ದಿನಗಳೊಳಗಿನ ದಿನಾಂಕವನ್ನು ಆಯ್ಕೆಮಾಡಿ." },
  nameError: { en: "Please enter your full name.", kn: "ದಯವಿಟ್ಟು ಪೂರ್ಣ ಹೆಸರನ್ನು ನಮೂದಿಸಿ." },
  emailError: { en: "Enter a valid email address.", kn: "ಸರಿಯಾದ ಇಮೇಲ್ ವಿಳಾಸವನ್ನು ನಮೂದಿಸಿ." },
  phoneError: { en: "Enter a valid phone number.", kn: "ಸರಿಯಾದ ದೂರವಾಣಿ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ." },
  limitError: { en: "Booking limit reached - maximum 2 bookings per email per month.", kn: "ಬುಕಿಂಗ್ ಮಿತಿ ತಲುಪಿದೆ - ತಿಂಗಳಿಗೆ ಒಂದು ಇಮೇಲ್‌ಗೆ ಗರಿಷ್ಠ 2 ಬುಕಿಂಗ್‌ಗಳು ಮಾತ್ರ." },
  takenError: { en: "Sorry, this room was just taken. Please choose another.", kn: "ಕ್ಷಮಿಸಿ, ಈ ಕೊಠಡಿ ಈಗಷ್ಟೇ ಬುಕ್ ಆಗಿದೆ. ದಯವಿಟ್ಟು ಇನ್ನೊಂದು ಆಯ್ಕೆಮಾಡಿ." },
  bookingSuccess: { en: "Booking confirmed! ID:", kn: "ಬುಕಿಂಗ್ ದೃಢೀಕರಿಸಲಾಗಿದೆ! ಸಂಖ್ಯೆ:" },
  copied: { en: "Booking ID copied", kn: "ಬುಕಿಂಗ್ ಸಂಖ್ಯೆ ನಕಲಿಸಲಾಗಿದೆ" },
};

const ROOM_COPY = {
  standard: {
    name: { en: "Standard Room", kn: "ಸ್ಟ್ಯಾಂಡರ್ಡ್ ಕೊಠಡಿ" },
    tagline: { en: "Comfortable, essential amenities", kn: "ಆರಾಮದಾಯಕ, ಅಗತ್ಯ ಸೌಲಭ್ಯಗಳು" },
    amenities: [
      { en: "Attached bathroom", kn: "ಲಗತ್ತಿಸಿದ ಸ್ನಾನಗೃಹ" },
      { en: "Cot with mattress", kn: "ಹಾಸಿಗೆಯೊಂದಿಗೆ ಮಂಚ" },
      { en: "Ceiling fan", kn: "ಸೀಲಿಂಗ್ ಫ್ಯಾನ್" },
      { en: "Drinking water", kn: "ಕುಡಿಯುವ ನೀರು" },
      { en: "Bed linen", kn: "ಹಾಸಿಗೆ ಬಟ್ಟೆ" },
    ],
  },
  deluxe: {
    name: { en: "Deluxe Room", kn: "ಡೆಲಕ್ಸ್ ಕೊಠಡಿ" },
    tagline: { en: "Spacious with additional comfort", kn: "ವಿಶಾಲ ಮತ್ತು ಹೆಚ್ಚುವರಿ ಆರಾಮ" },
    amenities: [
      { en: "Attached bathroom with hot water", kn: "ಬಿಸಿ ನೀರಿನೊಂದಿಗೆ ಲಗತ್ತಿಸಿದ ಸ್ನಾನಗೃಹ" },
      { en: "Queen-size bed", kn: "ಕ್ವೀನ್ ಗಾತ್ರದ ಹಾಸಿಗೆ" },
      { en: "Air conditioning", kn: "ಏರ್ ಕಂಡೀಷನಿಂಗ್" },
      { en: "Study desk", kn: "ಅಧ್ಯಯನ ಮೇಜು" },
      { en: "Complimentary tea/coffee", kn: "ಉಚಿತ ಚಹಾ/ಕಾಫಿ" },
    ],
  },
  family: {
    name: { en: "Family Suite", kn: "ಕುಟುಂಬ ಸೂಟ್" },
    tagline: { en: "For families & small groups", kn: "ಕುಟುಂಬಗಳು ಮತ್ತು ಸಣ್ಣ ಗುಂಪುಗಳಿಗೆ" },
    amenities: [
      { en: "Two bedrooms", kn: "ಎರಡು ಮಲಗುವ ಕೊಠಡಿಗಳು" },
      { en: "Living area", kn: "ವಾಸದ ಪ್ರದೇಶ" },
      { en: "Attached bathroom (hot water)", kn: "ಲಗತ್ತಿಸಿದ ಸ್ನಾನಗೃಹ (ಬಿಸಿ ನೀರು)" },
      { en: "Air conditioning", kn: "ಏರ್ ಕಂಡೀಷನಿಂಗ್" },
      { en: "Refrigerator & tea maker", kn: "ಫ್ರಿಜ್ ಮತ್ತು ಚಹಾ ತಯಾರಕ" },
    ],
  },
};

const RULES_KN = [
  "ಬುಕಿಂಗ್ ಒಂದು ದಿನಕ್ಕೆ ಮಾತ್ರ (ಚೆಕ್-ಇನ್‌ನಿಂದ 23 ಗಂಟೆಗಳು).",
  "ಬುಕಿಂಗ್ ನಂತರ ರದ್ದು, ಮರುಪಾವತಿ ಅಥವಾ ದಿನಾಂಕ / ಸಮಯ ಬದಲಾವಣೆ ಅನುಮತಿಸಲಾಗುವುದಿಲ್ಲ.",
  "ವಾಸದ ವಿಸ್ತರಣೆ ಅನುಮತಿಸಲಾಗುವುದಿಲ್ಲ. ಚೆಕ್-ಔಟ್ ಸಮಯ ಕಟ್ಟುನಿಟ್ಟಾಗಿದೆ.",
  "ಒಬ್ಬ ಬಳಕೆದಾರರಿಗೆ (ಇಮೇಲ್) ತಿಂಗಳಿಗೆ ಗರಿಷ್ಠ ಎರಡು ಕೊಠಡಿ ಬುಕಿಂಗ್‌ಗಳು.",
  "ಬುಕಿಂಗ್ ಅವಧಿ: 3 ರಿಂದ 30 ದಿನಗಳ ಮುಂಗಡ.",
  "ಮರುಪಾವತಿಸಬಹುದಾದ ₹500 ಠೇವಣಿ ಚೆಕ್-ಇನ್ ಕೌಂಟರ್‌ನಲ್ಲಿ ಪಾವತಿಸಬೇಕು.",
  "ನೀತಿಯ ಅವಶ್ಯಕತೆ ಇದ್ದಲ್ಲಿ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠವು ಬುಕಿಂಗ್‌ಗಳನ್ನು ರದ್ದುಪಡಿಸಿ ಮರುಪಾವತಿಸಬಹುದು.",
];

// Deterministic seed by date so availability is stable across refreshes
const seed = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};
const getAvailabilityForDate = (iso, existingBookings) => {
  const s = seed(iso);
  const bookedByType = existingBookings
    .filter((b) => b.checkInDate === iso)
    .reduce((acc, b) => {
      acc[b.roomTypeId] = (acc[b.roomTypeId] || 0) + 1;
      return acc;
    }, {});
  return ROOM_TYPES.map((r) => {
    // deterministic "already booked" count from seed (0..2)
    const seedBooked = (s >> (r.id === "standard" ? 0 : r.id === "deluxe" ? 3 : 6)) & 0b11;
    const held = bookedByType[r.id] || 0;
    const remaining = Math.max(ROOM_INVENTORY[r.id] - seedBooked - held, 0);
    return { ...r, remaining };
  });
};

const RoomBooking = () => {
  const { t, lang } = useLang();
  const L = (copy) => (copy && copy[lang]) || copy.en;
  const roomText = (room, key) => ROOM_COPY[room.id]?.[key]?.[lang] || ROOM_COPY[room.id]?.[key]?.en || room[key];
  const roomAmenities = (room) => ROOM_COPY[room.id]?.amenities?.map((item) => L(item)) || room.amenities;
  const rules = lang === "kn" ? RULES_KN : BOOKING_RULES;
  const [step, setStep] = useState("date"); // date | rooms | form | confirmed
  const [checkInDate, setCheckInDate] = useState(addDaysISO(3));
  const [availability, setAvailability] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [confirmation, setConfirmation] = useState(null);

  const minDate = useMemo(() => addDaysISO(3), []);
  const maxDate = useMemo(() => addDaysISO(30), []);

  const checkAvailability = () => {
    if (!checkInDate) return;
    if (checkInDate < minDate || checkInDate > maxDate) {
      toast.error(L(COPY.dateRangeError));
      return;
    }
    const list = getAvailabilityForDate(checkInDate, loadBookings());
    setAvailability(list);
    setStep("rooms");
  };

  const pickRoom = (room) => {
    if (room.remaining <= 0) return;
    setSelectedRoom(room);
    setErrors({});
    setStep("form");
  };

  const validateForm = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 3) e.name = L(COPY.nameError);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = L(COPY.emailError);
    if (!/^[+\d][\d\s-]{7,15}$/.test(form.phone)) e.phone = L(COPY.phoneError);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const confirmBooking = () => {
    if (!validateForm()) return;
    // Enforce max 2 bookings per email per calendar month of check-in
    const emailKey = form.email.trim().toLowerCase();
    const bookings = loadBookings();
    const targetMonth = checkInDate.slice(0, 7);
    const sameMonthCount = bookings.filter(
      (b) => b.email.toLowerCase() === emailKey && b.checkInDate.slice(0, 7) === targetMonth
    ).length;
    if (sameMonthCount >= 2) {
      toast.error(L(COPY.limitError));
      return;
    }

    // Re-check inventory just before locking
    const currentAvail = getAvailabilityForDate(checkInDate, bookings).find(
      (r) => r.id === selectedRoom.id
    );
    if (!currentAvail || currentAvail.remaining <= 0) {
      toast.error(L(COPY.takenError));
      return;
    }

    const bookingId = genBookingId();
    const checkOutDate = addHours(checkInDate, 23);
    const record = {
      bookingId,
      name: form.name.trim(),
      email: emailKey,
      phone: form.phone.trim(),
      roomTypeId: selectedRoom.id,
      roomTypeName: roomText(selectedRoom, "name"),
      price: selectedRoom.price,
      checkInDate,
      checkOutISO: checkOutDate.toISOString(),
      createdAt: new Date().toISOString(),
    };
    saveBookings([...bookings, record]);
    setConfirmation(record);
    setStep("confirmed");
    toast.success(`${L(COPY.bookingSuccess)} ${bookingId}`);
  };

  const restart = () => {
    setStep("date");
    setAvailability(null);
    setSelectedRoom(null);
    setForm({ name: "", email: "", phone: "" });
    setErrors({});
    setConfirmation(null);
  };

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("rooms.hero.eyebrow")}
        title={t("rooms.hero.title")}
        subtitle={t("rooms.hero.subtitle")}
        image={IMG.templeCorridor}
      />

      {/* Stepper */}
      <section className="py-3 sm:py-8 bg-[#FBF7F0]/80 border-b border-[#D4AF37]/20 sticky top-0 z-20 backdrop-blur-sm" data-testid="booking-stepper">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-8 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-6">
          <StepPill n="1" label={L(COPY.chooseDate)} active={step === "date"} done={["rooms", "form", "confirmed"].includes(step)} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="2" label={L(COPY.selectRoom)} active={step === "rooms"} done={["form", "confirmed"].includes(step)} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="3" label={L(COPY.yourDetails)} active={step === "form"} done={step === "confirmed"} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="4" label={L(COPY.confirmation)} active={step === "confirmed"} done={step === "confirmed"} />
        </div>
      </section>

      {/* STEP 1: Date */}
      {step === "date" && (
        <section className="py-16 sm:py-20" data-testid="step-date">
          <div className="max-w-3xl mx-auto px-4 lg:px-8">
            <SectionHeading eyebrow={L(COPY.step1)} title={L(COPY.chooseCheckIn)} align="center" />
            <div className="mt-10 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-6 sm:p-8 shadow-lg relative">
              <div className="absolute -top-6 -right-6 pointer-events-none">
                <Mandala size={140} color="#D4AF37" opacity={0.35} />
              </div>
              <label className="block text-sm font-semibold text-[#3B2A1A] mb-2">
                <CalendarDays className="inline w-4 h-4 mr-2 -mt-1 text-[#B65A14]" />
                {L(COPY.checkInDate)}
              </label>
              <input
                type="date"
                min={minDate}
                max={maxDate}
                value={checkInDate}
                onChange={(e) => setCheckInDate(e.target.value)}
                className="w-full rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                data-testid="checkin-input"
              />
              <p className="mt-2 text-xs text-[#3B2A1A]/70">
                {L(COPY.checkoutInfo)} {formatLongDate(minDate, lang)} - {formatLongDate(maxDate, lang)}.
              </p>

              <div className="mt-6 p-4 rounded-xl bg-[#F2E7D5]/70 border border-[#D4AF37]/30">
                <div className="flex items-start gap-2 text-xs text-[#3B2A1A]/85">
                  <AlertCircle className="w-4 h-4 text-[#B65A14] mt-0.5 shrink-0" />
                  <ul className="space-y-1 list-disc list-inside">
                    {rules.slice(0, 4).map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                onClick={checkAvailability}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3.5 text-sm font-semibold hover:bg-[#8F4610] transition shadow"
                data-testid="check-availability-btn"
              >
                {L(COPY.checkAvailability)} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* STEP 2: Rooms */}
      {step === "rooms" && availability && (
        <section className="py-16 sm:py-20" data-testid="step-rooms">
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            <SectionHeading eyebrow={L(COPY.step2)} title={`${L(COPY.availableRoomsFor)} ${formatLongDate(checkInDate, lang)}`} align="center" />
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {availability.map((r) => {
                const soldOut = r.remaining <= 0;
                return (
                  <div key={r.id} className="rounded-2xl overflow-hidden border border-[#D4AF37]/30 bg-[#FBF7F0] card-lift flex flex-col" data-testid={`room-card-${r.id}`}>
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="font-serif-display text-2xl text-[#3B2A1A]">{roomText(r, "name")}</div>
                      <div className="text-sm text-[#B65A14] italic">{roomText(r, "tagline")}</div>
                      <div className="flex items-center gap-3 mt-3 text-xs text-[#3B2A1A]/75">
                        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {L(COPY.upTo)} {r.capacity}</span>
                        <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {L(COPY.stay23)}</span>
                      </div>
                      <ul className="mt-4 space-y-1 text-sm text-[#3B2A1A]/85 list-disc list-inside">
                        {roomAmenities(r).map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                      <div className="mt-5 flex items-end justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-[#3B2A1A]/60">{L(COPY.roomRate)}</div>
                          <div className="font-serif-display text-2xl text-[#3B2A1A] flex items-center">
                            <IndianRupee className="w-5 h-5" />{r.price}
                            <span className="text-xs font-normal text-[#3B2A1A]/70 ml-1">{L(COPY.per23)}</span>
                          </div>
                        </div>
                        <div className={`text-xs font-semibold px-3 py-1 rounded-full ${soldOut ? "bg-[#A81F1F]/15 text-[#A81F1F]" : "bg-[#D4AF37]/25 text-[#8F4610]"}`}>
                          {soldOut ? L(COPY.soldOut) : `${r.remaining} ${L(COPY.available)}`}
                        </div>
                      </div>
                      <button
                        disabled={soldOut}
                        onClick={() => pickRoom(r)}
                        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition ${
                          soldOut
                            ? "bg-[#F2E7D5] text-[#3B2A1A]/40 cursor-not-allowed"
                            : "bg-[#B65A14] text-[#FBF7F0] hover:bg-[#8F4610]"
                        }`}
                        data-testid={`select-room-${r.id}`}
                      >
                        {soldOut ? L(COPY.unavailable) : (<>{L(COPY.selectRoom)} <ArrowRight className="w-4 h-4" /></>)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setStep("date")}
                className="text-sm text-[#B65A14] hover:text-[#8F4610] underline"
                data-testid="change-date-btn"
              >
                ← {L(COPY.changeDate)}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* STEP 3: Details form */}
      {step === "form" && selectedRoom && (
        <section className="py-16 sm:py-20" data-testid="step-form">
          <div className="max-w-4xl mx-auto px-4 lg:px-8">
            <SectionHeading eyebrow={L(COPY.step3)} title={L(COPY.bookingDetails)} align="center" />
            <div className="mt-10 grid md:grid-cols-5 gap-6">
              <div className="md:col-span-3 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-6 sm:p-8">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.fullName)}</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder={L(COPY.namePlaceholder)}
                      data-testid="name-input"
                    />
                    {errors.name && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-name">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.email)}</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder="name@example.com"
                      data-testid="email-input"
                    />
                    {errors.email && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.phone)}</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder="+91 98xxxxxxxx"
                      data-testid="phone-input"
                    />
                    {errors.phone && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-phone">{errors.phone}</p>}
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-xl bg-[#F2E7D5]/70 border border-[#D4AF37]/30">
                  <div className="overline text-[#B65A14] mb-2">{L(COPY.important)}</div>
                  <ul className="text-xs text-[#3B2A1A]/85 space-y-1 list-disc list-inside">
                    {rules.map((r) => (<li key={r}>{r}</li>))}
                  </ul>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => setStep("rooms")}
                    className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold hover:bg-[#F2E7D5] transition"
                    data-testid="back-to-rooms-btn"
                  >
                    ← {L(COPY.back)}
                  </button>
                  <button
                    onClick={confirmBooking}
                    className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-7 py-3 text-sm font-semibold hover:bg-[#8F4610] transition shadow"
                    data-testid="confirm-booking-btn"
                  >
                    {L(COPY.confirmBooking)} <CheckCircle2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Summary */}
              <aside className="md:col-span-2 bg-gradient-to-b from-[#FBF7F0] to-[#F2E7D5] border border-[#D4AF37]/30 rounded-2xl p-6 h-fit sticky md:top-24" data-testid="booking-summary">
                <div className="font-cinzel text-xs tracking-widest text-[#B65A14]">{L(COPY.bookingSummary)}</div>
                <div className="mt-3 space-y-2 text-sm text-[#3B2A1A]">
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.room)}</span><span className="font-semibold text-right">{roomText(selectedRoom, "name")}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.checkIn)}</span><span className="font-semibold text-right">{formatLongDate(checkInDate, lang)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.checkOut)}</span><span className="font-semibold text-right">{addHours(checkInDate, 23).toLocaleString(lang === "kn" ? "kn-IN" : "en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.duration)}</span><span className="font-semibold">{L(COPY.hours23)}</span></div>
                </div>
                <div className="mt-4 border-t border-[#D4AF37]/40 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#3B2A1A]/70">{L(COPY.roomRate)}</span><span className="font-semibold flex items-center"><IndianRupee className="w-3.5 h-3.5" />{selectedRoom.price}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.depositAtCheckIn)}</span><span className="font-semibold flex items-center"><IndianRupee className="w-3.5 h-3.5" />{REFUNDABLE_DEPOSIT}</span></div>
                </div>
                <div className="mt-4 border-t border-[#D4AF37]/40 pt-4 flex justify-between items-baseline">
                  <span className="text-xs uppercase tracking-widest text-[#3B2A1A]/70">{L(COPY.payableNow)}</span>
                  <span className="font-serif-display text-2xl text-[#3B2A1A] flex items-center"><IndianRupee className="w-5 h-5" />{selectedRoom.price}</span>
                </div>
                <p className="mt-3 text-[11px] text-[#3B2A1A]/70">{L(COPY.depositNote)} ₹{REFUNDABLE_DEPOSIT} {L(COPY.depositNoteEnd)}</p>
              </aside>
            </div>
          </div>
        </section>
      )}

      {/* STEP 4: Confirmation */}
      {step === "confirmed" && confirmation && (
        <section className="py-16 sm:py-20" data-testid="step-confirmed">
          <div className="max-w-3xl mx-auto px-4 lg:px-8">
            <div className="bg-[#FBF7F0] border border-[#D4AF37]/40 rounded-2xl p-8 relative overflow-hidden shadow-lg">
              <div className="absolute -top-8 -right-8 pointer-events-none">
                <Mandala size={200} color="#D4AF37" opacity={0.35} />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/30 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[#8F4610]" />
                </div>
                <div>
                  <div className="overline text-[#B65A14]">{t("site.name")}</div>
                  <h3 className="font-serif-display text-3xl text-[#3B2A1A]">{L(COPY.bookingConfirmed)}</h3>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between bg-[#F2E7D5]/70 border border-[#D4AF37]/40 rounded-xl px-4 py-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#3B2A1A]/70">{L(COPY.bookingId)}</div>
                  <div className="font-cinzel text-lg text-[#3B2A1A]" data-testid="confirmation-id">{confirmation.bookingId}</div>
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(confirmation.bookingId); toast.success(L(COPY.copied)); }}
                  className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-3 py-1.5 text-xs font-semibold hover:bg-[#FBF7F0]"
                  data-testid="copy-id-btn"
                >
                  <Copy className="w-3.5 h-3.5" /> {L(COPY.copy)}
                </button>
              </div>

              <dl className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.guest)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.name}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.email)}</dt><dd className="font-semibold text-[#3B2A1A] break-all">{confirmation.email}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.phone)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.phone}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.room)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.roomTypeName}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.checkIn)}</dt><dd className="font-semibold text-[#3B2A1A]">{formatLongDate(confirmation.checkInDate, lang)}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.checkOut)}</dt><dd className="font-semibold text-[#3B2A1A]">{new Date(confirmation.checkOutISO).toLocaleString(lang === "kn" ? "kn-IN" : "en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.duration)}</dt><dd className="font-semibold text-[#3B2A1A]">{L(COPY.hours23)}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.roomFare)}</dt><dd className="font-semibold text-[#3B2A1A]">₹{confirmation.price}</dd></div>
              </dl>

              <div className="mt-6 p-4 rounded-xl bg-[#F2E7D5]/70 border border-[#D4AF37]/30">
                <div className="flex items-start gap-2 text-sm text-[#3B2A1A]/90">
                  <AlertCircle className="w-4 h-4 text-[#B65A14] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-[#3B2A1A]">{L(COPY.depositPayable)} ₹{REFUNDABLE_DEPOSIT} {L(COPY.depositPayableEnd)}</div>
                    <div className="text-xs text-[#3B2A1A]/80 mt-1">
                      {L(COPY.carryProof)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold hover:bg-[#8F4610] transition shadow"
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4" /> {L(COPY.printConfirmation)}
                </button>
                <button
                  onClick={restart}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold hover:bg-[#F2E7D5] transition"
                  data-testid="book-again-btn"
                >
                  {L(COPY.anotherBooking)}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </PageLayout>
  );
};

export default RoomBooking;
